import React, { useState } from "react";
import { Link } from "react-router-dom";

import { PartnerCenterEnrollmentStatus } from "@gamedev-web/one-gaming-crm";

import { LegalEntityNameChangeStatus } from "../../../../components/LegalEntityNameChangeStatus";
import { appDeveloperAgreement, xboxOnboardSiteLocationPrefix } from "../../../../constants";
import { useString, useStrings } from "../../../../localization/localizationHelpers";
import type { LegalEntityNameChangeState } from "../../../../server/crm/api/models/xboxOnboardModels";
import * as styles from "../../PartnerHomePage.module.css";
import { PartnerStudioHeader } from "../PartnerStudioHeader";

const SettingsLinkPlaceholder = "__PARTNER_SETTINGS_LINK__";

type PartnerEnrollmentCardProps = Readonly<{
  studioName: string | undefined;
  sellerId: string | undefined;
  isIdXboxApproved: boolean;
  enrollmentStatus: PartnerCenterEnrollmentStatus | undefined;
  isEnrollmentRejected: boolean;
  isEnrollmentInProgress: boolean;
  isDunsRequired: boolean;
  legalEntityNameChange?: LegalEntityNameChangeState;
  contactEmail?: string;
  hasAttachedBanner: boolean;
  showDevReadinessGuideOption: boolean;
  onShowDevReadinessGuide: () => void;
  onStartEnrollment: () => void;
  onRetryEnrollment: () => void;
}>;

export const PartnerEnrollmentCard = ({
  studioName,
  sellerId,
  isIdXboxApproved,
  enrollmentStatus,
  isEnrollmentRejected,
  isEnrollmentInProgress,
  isDunsRequired,
  legalEntityNameChange,
  contactEmail,
  hasAttachedBanner,
  showDevReadinessGuideOption,
  onShowDevReadinessGuide,
  onStartEnrollment,
  onRetryEnrollment,
}: PartnerEnrollmentCardProps) => {
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [
    rejectedTitle,
    retryButton,
    signAdaTitle,
    signAdaDescription,
    appDeveloperAgreementLabel,
    enrollButton,
    processingButton,
    settingsLabel,
  ] = useStrings(
    "partnerHomeEnrollmentRejectedTitle",
    "partnerHomeRetryButton",
    "partnerLegalAppDisclosureAgreementTitle",
    "partnerLegalAppDisclosureAgreementDescription",
    "appDeveloperAgreementLink",
    "idXboxGdkEnroll",
    "processingButton",
    "partnerSettingsPageTitle"
  );
  const rejectedGuidance = useString(
    isDunsRequired ? "partnerEnrollmentDunsRequiredGuidance" : "partnerEnrollmentRejectedGuidance",
    SettingsLinkPlaceholder
  );
  const [rejectedGuidanceStart, rejectedGuidanceEnd = ""] =
    rejectedGuidance.split(SettingsLinkPlaceholder);
  const termsAcceptanceLabel = useString(
    "partnerHomeTermsAcceptanceLabel",
    appDeveloperAgreementLabel
  );
  const [termsAcceptanceBeforeLink, termsAcceptanceAfterLink = ""] = termsAcceptanceLabel.split(
    appDeveloperAgreementLabel
  );

  const renderTerms = (checked: boolean, disabled: boolean) => (
    <label className={styles.termsCheckboxContainer}>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={disabled ? undefined : () => setTermsAccepted(previous => !previous)}
        className={styles.checkbox}
      />
      <span className={styles.termsLabel}>
        {termsAcceptanceBeforeLink}
        <a
          href={appDeveloperAgreement}
          className={styles.agreementLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          {appDeveloperAgreementLabel}
        </a>
        {termsAcceptanceAfterLink}
      </span>
    </label>
  );

  return (
    <div className={styles.cardDashed}>
      <div className={styles.cardDashedTop}>
        <PartnerStudioHeader
          studioName={studioName}
          sellerId={sellerId}
          isIdXboxApproved={isIdXboxApproved}
          isEnrollmentComplete={false}
          isEnrollmentEarlyComplete={false}
          showDevReadinessGuideOption={showDevReadinessGuideOption}
          showSettingsOption={true}
          onShowDevReadinessGuide={onShowDevReadinessGuide}
        />
        {legalEntityNameChange && (
          <LegalEntityNameChangeStatus
            state={legalEntityNameChange}
            contactEmail={contactEmail}
            attached
            flush
          />
        )}
      </div>
      <div
        className={`${styles.cardDashedBottom} ${
          isEnrollmentRejected ? styles.cardDashedBottomRejected : ""
        } ${hasAttachedBanner ? styles.cardDashedBottomAttached : ""}`}
      >
        {isEnrollmentRejected ? (
          <div className={styles.enrollmentRejectedBanner}>
            <span className={styles.rejectedIcon}>⚠</span>
            <div className={styles.rejectedText}>
              <span className={styles.rejectedTitle}>{rejectedTitle}</span>
              <span className={styles.rejectedSubtitle}>
                {rejectedGuidanceStart}
                <Link
                  className={styles.rejectedSettingsLink}
                  to={`${xboxOnboardSiteLocationPrefix}/settings`}
                >
                  {settingsLabel}
                </Link>
                {rejectedGuidanceEnd}
              </span>
            </div>
            <button className={styles.retryButton} onClick={onRetryEnrollment}>
              {retryButton}
            </button>
          </div>
        ) : (
          <>
            <div className={styles.enrollmentHeader}>
              <span className={styles.statusDotYellow} aria-hidden="true" />
              <h3 className={styles.enrollmentTitleInline}>{signAdaTitle}</h3>
            </div>
            <p className={styles.enrollmentDescription}>{signAdaDescription}</p>
            {(enrollmentStatus === PartnerCenterEnrollmentStatus.Enabled ||
              enrollmentStatus === PartnerCenterEnrollmentStatus.Unset) && (
              <div className={styles.enrollmentActions}>
                {renderTerms(termsAccepted, false)}
                <button
                  className={styles.enrollButton}
                  disabled={!termsAccepted}
                  onClick={onStartEnrollment}
                >
                  {enrollButton}
                </button>
              </div>
            )}
            {isEnrollmentInProgress && (
              <div className={styles.enrollmentActions}>
                {renderTerms(true, true)}
                <button className={styles.processingButton} disabled={true}>
                  <span className={styles.processingSpinner} aria-hidden="true" />
                  {processingButton}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
